import React from 'react';
import { Box, TextField, IconButton, Tooltip } from '@mui/material';
import { Autocomplete } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';

/**
 * Font picker with search, grouping, delete custom fonts, and upload button.
 *
 * Props:
 *   allFontOptions – array of { label, value, group, filename? }
 *   handleFontDelete – (filename) => void
 *   fontInputRef – ref forwarded to the hidden file <input> rendered by the parent
 *   value, onChange, disabled, size, sx – standard picker props
 */
function FontPicker({ value, onChange, disabled, size = 'small', sx = {}, allFontOptions = [], handleFontDelete, fontInputRef }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ...sx }}>
      <Autocomplete
        size={size}
        disabled={disabled}
        options={allFontOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        value={
          allFontOptions.find((o) => o.value === value) ||
          (value ? { label: value, value, group: 'Systeem fonts' } : null)
        }
        onChange={(_, newVal) => onChange(newVal ? newVal.value : '')}
        isOptionEqualToValue={(option, val) =>
          option.value === (typeof val === 'string' ? val : val?.value)
        }
        renderInput={(params) => <TextField {...params} label="Font" />}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.value}
            sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
          >
            <span>{option.label}</span>
            {option.filename && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFontDelete(option.filename);
                }}
                sx={{ p: 0.25 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
        sx={{ minWidth: 180, flex: 1 }}
      />
      <Tooltip title="Font uploaden (.ttf / .otf)">
        <IconButton size="small" onClick={() => fontInputRef.current?.click()}>
          <UploadFileIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default FontPicker;
